import { useEffect, createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { trackSupportOpen } from '../lib/tracking';

/**
 * Estado da janela de atendimento.
 *
 * A janela mora no rodapé da página, mas quem a abre pode estar em qualquer
 * lugar — o item "Dúvidas" do menu da conta, por exemplo, fica dentro do
 * cabeçalho. Um contexto evita passar a função de mão em mão por toda a
 * árvore só para dois pontos distantes se falarem.
 */
interface SupportContexto {
  aberto: boolean;
  abrir: () => void;
  fechar: () => void;
  alternar: () => void;
}

const Ctx = createContext<SupportContexto | null>(null);

export function SupportProvider({ children }: { children: ReactNode }) {
  const [aberto, setAberto] = useState(false);

  // Conta a abertura pelo estado, e não nos botões: o atendimento abre pelo
  // widget, pela rota /duvidas e por atalhos, e todos passam por aqui.
  useEffect(() => {
    if (aberto) trackSupportOpen();
  }, [aberto]);

  const valor = useMemo<SupportContexto>(
    () => ({
      aberto,
      abrir: () => setAberto(true),
      fechar: () => setAberto(false),
      alternar: () => setAberto((v) => !v),
    }),
    [aberto],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSupport(): SupportContexto {
  const ctx = useContext(Ctx);
  // Fora do provider a janela simplesmente não existe; devolver um estado
  // inerte deixa o componente chamador funcionar em vez de derrubar a página.
  const inerte = useCallback(() => undefined, []);
  return ctx ?? { aberto: false, abrir: inerte, fechar: inerte, alternar: inerte };
}
